import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicDetail } from '../../../lib/api/epics';
import type { EpicDetail, EpicSession } from '../../../../../shared/types/epics';
import { prNumberFromSessionStoryId } from '../../../../../shared/types/epics';
import { Header } from '../../../components/layout/Header';
import { UrlTabs, useUrlTab, type UrlTabItem } from '../../../components/ui';
import { TranscriptDrawer } from '../../../components/epicDetail/TranscriptDrawer';
import { StuckDiagnosisCard } from '../../../components/epicDetail/StuckDiagnosisCard';
import { ActionCenter } from '../../../components/epicDetail/ActionCenter';
import { PendingWorkBanner } from '../../../components/epicDetail/PendingWorkBanner';
import { EpicSummaryHeader } from './header/EpicSummaryHeader';
import { PlanReviewCard } from './plan/PlanReviewCard';
import { ProgressTab } from './tabs/ProgressTab';
import { PlanTab } from './tabs/PlanTab';
import { ChangesTab } from './tabs/ChangesTab';
import { QualityTab } from './tabs/QualityTab';
import { CostTab } from './tabs/CostTab';
import { ActivityTab } from './tabs/ActivityTab';

// Re-exported so the pre-existing __tests__/epicDetail/{safety-net-panel,
// migration-panel}.test.tsx (which import these two named exports from this
// page module) keep working after the UX-05 split moved their definitions
// into stories/SafetyNetPanel.tsx and stories/MigrationPanel.tsx.
export { SafetyNetPanel } from './stories/SafetyNetPanel';
export { MigrationPanel } from './stories/MigrationPanel';

const POLL_INTERVAL_MS = 15_000;
// FORGE-206: while an agent is actually in flight, poll faster so the live card
// (iteration / cost / last action) updates smoothly. Falls back to the slow
// cadence when the epic is idle.
const ACTIVE_POLL_INTERVAL_MS = 5_000;

/** True when an epic-level agent (planner/PM) or any story has a fresh in-flight agent or in-progress state. */
function epicHasActiveAgent(detail: EpicDetail | undefined): boolean {
  if (!detail) return false;
  // Epic-level agents (epic-planner / product-manager / solution-analyst) that
  // have no story row — keep polling while one is live so the page refreshes.
  if (detail.liveAgents?.some((la) => !la.stale)) return true;
  return detail.stories.some(
    (s) =>
      (s.liveAgents?.some((la) => !la.stale) ?? false) ||
      (s.liveAgent != null && !s.liveAgent.stale) ||
      /(_IN_PROGRESS|_RUNNING)$/.test(s.state),
  );
}

/** UX-05 Progress tab badge: a red dot when any story is failed / blocked /
 *  paused-needs-human — mirrors the state groups storyActionSet already uses
 *  to decide "this story needs an operator". */
function anyStoryNeedsAttention(detail: EpicDetail): boolean {
  return detail.stories.some(
    (s) => s.state.endsWith('_FAILED') || s.state === 'BLOCKED' || s.state === 'PAUSED_NEEDS_HUMAN',
  );
}

const TAB_IDS = ['progress', 'activity', 'plan', 'changes', 'quality', 'cost'] as const;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function EpicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const epicId = id ?? '';

  const [open, setOpen] = useState<
    | { storyId: string; agentType: string; taskArn?: string | null }
    | null
  >(null);

  // Pause control surfaced inside the EpicPipeline. When paused, both
  // the React-Query refetch loop and the pipeline's own now-cursor freeze.
  const [paused, setPaused] = useState(false);

  const query = useQuery({
    queryKey: ['epic', epicId],
    queryFn: () => fetchEpicDetail(epicId),
    enabled: epicId.length > 0,
    refetchInterval: (q) => {
      if (paused) return false;
      return epicHasActiveAgent(q.state.data) ? ACTIVE_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    },
    staleTime: ACTIVE_POLL_INTERVAL_MS,
  });

  const sessionsByStory = useMemo(() => {
    if (!query.data) return new Map<string, EpicSession[]>();
    const m = new Map<string, EpicSession[]>();
    for (const s of query.data.sessions) {
      const list = m.get(s.storyId) ?? [];
      list.push(s);
      m.set(s.storyId, list);
    }
    return m;
  }, [query.data]);

  // Sessions whose storyId isn't backed by a STORY# row — e.g. "epic",
  // "plan", legacy story ids. Surface them in their own card so they
  // aren't silently dropped.
  const orphanSessions = useMemo(() => {
    if (!query.data) return [] as EpicSession[];
    const knownIds = new Set(query.data.stories.map((s) => s.id));
    // Exclude PR-tracking runs (pr-fix / pr-resolution-*) — they're now shown
    // under their PR card, so listing them here too would double-render them.
    return query.data.sessions.filter(
      (s) => !knownIds.has(s.storyId) && prNumberFromSessionStoryId(s.storyId) === null,
    );
  }, [query.data]);

  // Two-phase cost split: Build = agent runs attached to stories; Review = the
  // PR-tracking runs attached to each PR (pr-fix / pr-resolution-*).
  const buildCostUsd = useMemo(() => {
    let sum = 0;
    for (const list of sessionsByStory.values()) for (const s of list) sum += s.costUsd ?? 0;
    return sum;
  }, [sessionsByStory]);
  const reviewCostUsd = useMemo(() => {
    if (!query.data) return 0;
    const ws = (query.data.prWatches && query.data.prWatches.length > 0)
      ? query.data.prWatches
      : (query.data.prWatch ? [query.data.prWatch] : []);
    let sum = 0;
    for (const w of ws) for (const s of (w.sessions ?? [])) sum += s.costUsd ?? 0;
    return sum;
  }, [query.data]);

  // UX-05: Quality tab's badge count comes from SecurityFindings (self-fetching,
  // mounted only while the Quality tab is open — see QualityTab/onFindingsCount)
  // since open-finding counts aren't part of the epic payload.
  const [qualityCount, setQualityCount] = useState<number | undefined>(undefined);

  const [activeTab, setActiveTab] = useUrlTab('tab', TAB_IDS, 'progress');

  // UX-05 §5: a `#story-…` deep link always opens on Progress, whatever tab
  // the URL otherwise named, and scrolls to the story once it's rendered.
  useEffect(() => {
    if (location.hash && activeTab !== 'progress') setActiveTab('progress');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  useEffect(() => {
    if (!location.hash || activeTab !== 'progress' || !query.isSuccess) return;
    const targetId = location.hash.slice(1);
    // Give the Progress tab's content a tick to mount before scrolling.
    const t = window.setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [location.hash, activeTab, query.isSuccess]);

  const changesBadge: number | true | undefined = useMemo(() => {
    if (!query.data) return undefined;
    if (query.data.prTailState === 'ESCALATED') return true;
    const watches = (query.data.prWatches && query.data.prWatches.length > 0)
      ? query.data.prWatches
      : (query.data.prWatch ? [query.data.prWatch] : []);
    return watches.length > 0 ? watches.length : undefined;
  }, [query.data]);

  const progressBadge: true | undefined =
    query.data && anyStoryNeedsAttention(query.data) ? true : undefined;

  const tabs: UrlTabItem[] = [
    { id: 'progress', label: 'Progress', badge: progressBadge },
    { id: 'activity', label: 'Activity' },
    { id: 'plan', label: 'Plan' },
    { id: 'changes', label: 'Changes', badge: changesBadge },
    { id: 'quality', label: 'Quality', badge: qualityCount && qualityCount > 0 ? qualityCount : undefined },
    { id: 'cost', label: 'Cost' },
  ];

  const onOpenTranscript = (storyId: string, agentType: string, taskArn?: string | null) =>
    setOpen({ storyId, agentType, taskArn });

  return (
    <div data-testid="epic-detail-page" className="min-h-screen bg-slate-50">
      <Header title="Epic" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Link
          to="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          className="text-xs text-primary-500 hover:text-primary-600 hover:underline"
        >
          ← Back to epics
        </Link>

        {query.isLoading && <Skeleton />}
        {query.isError && <ErrorView error={query.error as Error} />}
        {query.isSuccess && query.data && (
          <>
            <EpicSummaryHeader epic={query.data} />
            {query.data.state === 'PLAN_POSTED' && (
              <PlanReviewCard epic={query.data} />
            )}
            <ActionCenter epic={query.data} epicRef={epicId} />
            <StuckDiagnosisCard diagnosis={query.data.diagnosis} />
            <PendingWorkBanner epicRef={epicId} />

            <UrlTabs param="tab" tabs={tabs} defaultId="progress" testIdPrefix="epic-tab" />

            {activeTab === 'progress' && (
              <ProgressTab
                epic={query.data}
                epicId={epicId}
                sessionsByStory={sessionsByStory}
                orphanSessions={orphanSessions}
                paused={paused}
                onTogglePaused={() => setPaused((p) => !p)}
                onOpenTranscript={onOpenTranscript}
              />
            )}
            {activeTab === 'activity' && <ActivityTab epicRef={epicId} />}
            {activeTab === 'plan' && <PlanTab epic={query.data} />}
            {activeTab === 'changes' && (
              <ChangesTab epic={query.data} epicId={epicId} onOpenTranscript={onOpenTranscript} />
            )}
            {activeTab === 'quality' && (
              <QualityTab epic={query.data} epicId={epicId} onFindingsCount={setQualityCount} />
            )}
            {activeTab === 'cost' && (
              <CostTab epic={query.data} buildCostUsd={buildCostUsd} reviewCostUsd={reviewCostUsd} />
            )}
          </>
        )}
      </main>

      {open && query.data && (
        <TranscriptDrawer
          open={!!open}
          onClose={() => setOpen(null)}
          epicId={epicId}
          storyId={open.storyId}
          agentType={open.agentType}
          taskArn={open.taskArn}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading / error
// ---------------------------------------------------------------------------

const Skeleton: React.FC = () => (
  <div data-testid="epic-detail-skeleton" className="animate-pulse space-y-4">
    <div className="h-7 bg-slate-200 rounded w-1/3" />
    <div className="h-4 bg-slate-200 rounded w-1/2" />
    <div className="h-32 bg-slate-200 rounded-xl" />
    <div className="h-48 bg-slate-200 rounded-xl" />
  </div>
);

const ErrorView: React.FC<{ error: Error }> = ({ error }) => {
  const isNotFound = error.message === 'NOT_FOUND';
  return (
    <div
      role="alert"
      className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2"
    >
      {isNotFound ? 'Epic not found.' : `Failed to load epic. ${error.message}`}
    </div>
  );
};

export default EpicDetailPage;
